/**
 * CDP Profile Navigation Utilities
 *
 * Handles data persistence when navigating to CDP profile page
 * Uses sessionStorage to ensure data survives page refresh and new tab navigation
 */

const CDP_PROFILE_STORAGE_KEY = 'cdp-profile-data';
export interface CDPProfileData {
  ccPatientId?: string;
  patientCCId?: string;
  cdpProfileId?: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  dateOfBirth?: string;
  email?: string;
  phoneNumber?: string;
  phone?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  zipcode?: string;
  gender?: string;
  status?: string;
  qualifiedProtocols?: string[] | string;
  associatedProtocols?: string[] | string;
  dedupeStatus?: string;
  matchPercentage?: number | string;
  siteName?: string;
  [key: string]: any;
}

/**
 * Store CDP profile data before navigation
 * Call this before navigating to the CDP profile page
 */
export const storeCDPProfileData = (data: CDPProfileData): void => {
  try {
    const storageData = {
      data,
      timestamp: Date.now(),
    };

    const jsonString = JSON.stringify(storageData);

    sessionStorage.setItem(CDP_PROFILE_STORAGE_KEY, jsonString);
  } catch (error) {
    console.error('[CDP Navigation] ❌ Failed to store profile data:', error);
    console.error('[CDP Navigation] Error details:', {
      name: (error as Error).name,
      message: (error as Error).message,
    });
  }
};

/**
 * Retrieve CDP profile data from storage
 * Returns null if data is expired or not found
 */
export const getCDPProfileData = (patientId?: string): CDPProfileData | null => {
  try {
    const stored = sessionStorage.getItem(CDP_PROFILE_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const { data } = JSON.parse(stored);

    const cachedId = data.cdpProfileId || data.ccPatientId || data.patientCCId || data.patientId;
    if (patientId && patientId !== 'CDP-001' && cachedId !== patientId) {
      return null;
    }
    return data;
  } catch (error) {
    console.error('[CDP Navigation] Failed to retrieve profile data:', error);
    return null;
  }
};
